import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import PWAInstallBanner from "@/components/PWAInstallBanner";

import Landing from "@/pages/Landing";
import Clubs from "@/pages/Clubs";
import ClubDetail from "@/pages/ClubDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import DashboardClub from "@/pages/DashboardClub";
import DashboardSponsor from "@/pages/DashboardSponsor";
import DashboardAdmin from "@/pages/DashboardAdmin";
import Concept from "@/pages/Concept";
import Impact from "@/pages/Impact";
import Realizations from "@/pages/Realizations";
import Panther from "@/pages/Panther";
import Payment from "@/pages/Payment";
import Legal from "@/pages/Legal";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Loyalty from "@/pages/Loyalty";
import Referral from "@/pages/Referral";
import SponsorAnalytics from "@/pages/SponsorAnalytics";
import CanonicalLink from "@/components/CanonicalLink";

function Layout() {
    return (
        <div className="min-h-screen flex flex-col bg-[#09090b] text-zinc-100">
            <CanonicalLink />
            <Navbar />
            <main className="flex-1">
                <Outlet />
            </main>
            <Footer />
            <Toaster theme="dark" position="top-right" />
            <PWAInstallBanner />
        </div>
    );
}

// Role arrays — module-level to prevent re-creation on each App render
const ROLE_CLUB = ["club"];
const ROLE_SPONSOR = ["sponsor"];
const ROLE_ADMIN = ["admin"];

function DashRedirect() {
    const { user, loading } = useAuth();
    if (loading) return <div className="p-12 text-zinc-500">Chargement…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (user.role === "club") return <Navigate to="/dashboard/club" replace />;
    if (user.role === "admin") return <Navigate to="/dashboard/admin" replace />;
    return <Navigate to="/dashboard/sponsor" replace />;
}

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Routes>
                    <Route element={<Layout />}>
                        <Route path="/" element={<Landing />} />
                        <Route path="/clubs" element={<Clubs />} />
                        <Route path="/clubs/:id" element={<ClubDetail />} />
                        <Route path="/concept" element={<Concept />} />
                        <Route path="/impact" element={<Impact />} />
                        <Route path="/realisations" element={<Realizations />} />
                        <Route path="/panther" element={<Panther />} />
                        <Route path="/payment" element={<Payment />} />
                        <Route path="/mentions-legales" element={<Legal />} />
                        <Route path="/cgu" element={<Terms />} />
                        <Route path="/confidentialite" element={<Privacy />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/dashboard" element={<DashRedirect />} />
                        <Route
                            path="/dashboard/club"
                            element={
                                <ProtectedRoute roles={ROLE_CLUB}>
                                    <DashboardClub />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/sponsor"
                            element={
                                <ProtectedRoute roles={ROLE_SPONSOR}>
                                    <DashboardSponsor />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/sponsor/loyalty"
                            element={
                                <ProtectedRoute roles={ROLE_SPONSOR}>
                                    <Loyalty />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/sponsor/referral"
                            element={
                                <ProtectedRoute roles={ROLE_SPONSOR}>
                                    <Referral />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/sponsor/analytics"
                            element={
                                <ProtectedRoute roles={ROLE_SPONSOR}>
                                    <SponsorAnalytics />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/admin"
                            element={
                                <ProtectedRoute roles={["admin"]}>
                                    <DashboardAdmin />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Route>
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
